/**
 * Owns the notes-filter state shared between the Notes panel, Reports
 * panel, and the map's note filtering. Exposes a `setNotesFilterState`
 * setter that defaults to a baseline range when called with no args, and
 * a stable `extendDateRangeToInclude` callback that effects can call
 * without re-firing on every filter change. The closed-over ref keeps
 * the latest filter visible to the stable callback without leaking the
 * ref to consumers.
 */

import * as I from 'immutable';
import React from 'react';

import {noteDateFilterOptions} from 'app/components/NotesReports/Filters';
import {ApiOrganization, getOrgNoteDateFilter} from 'app/modules/Remote/Organization';
import * as noteUtils from 'app/utils/noteUtils';

export const DEFAULT_NOTES_FILTER_STATE: noteUtils.NotesFilterState = {
  dateRange: noteDateFilterOptions['last_year'],
  noteTagIds: [],
};

interface Args {
  organization: I.ImmutableOf<ApiOrganization>;
}

export interface NotesFilterControls {
  notesFilterState: noteUtils.NotesFilterState;
  /**
   * Set the filter state. Passing `undefined` resets to
   * `DEFAULT_NOTES_FILTER_STATE`.
   */
  setNotesFilterState: (state?: noteUtils.NotesFilterState) => void;
  /**
   * Stable callback (identity preserved across renders). If `createdAt` is
   * older than the current filter's start date, extend the start date back
   * to include it. Reads the current filter via a closed-over ref so the
   * caller's effect doesn't have to re-fire on every filter change.
   */
  extendDateRangeToInclude: (createdAt: string) => void;
}

export default function useNotesFilterState({organization}: Args): NotesFilterControls {
  const [notesFilterState, setNotesFilterStateValue] = React.useState<noteUtils.NotesFilterState>(
    () => ({
      dateRange: noteDateFilterOptions[getOrgNoteDateFilter(organization)],
      noteTagIds: [],
    })
  );

  // Mirror the latest filter into a ref so `extendDateRangeToInclude` can
  // read the current value without re-creating its identity on every render.
  // The ref is hidden from consumers — they only see the stable callback.
  const filterRef = React.useRef(notesFilterState);
  filterRef.current = notesFilterState;

  const setNotesFilterState = React.useCallback(
    (state?: noteUtils.NotesFilterState) => {
      setNotesFilterStateValue(
        state ?? {
          dateRange: noteDateFilterOptions[getOrgNoteDateFilter(organization)],
          noteTagIds: [],
        }
      );
    },
    [organization]
  );

  const extendDateRangeToInclude = React.useCallback((createdAt: string) => {
    const current = filterRef.current;
    if (!current.dateRange[0]) return;
    if (createdAt >= current.dateRange[0].toISOString()) return;
    const extendedStartDate = new Date(createdAt);
    extendedStartDate.setDate(extendedStartDate.getDate() - 1);
    if (extendedStartDate.getTime() === current.dateRange[0].getTime()) return;
    setNotesFilterStateValue({
      ...current,
      dateRange: [extendedStartDate, null],
    });
  }, []);

  return {notesFilterState, setNotesFilterState, extendDateRangeToInclude};
}
